import { getNestedFieldValue } from '../../helpers/calculateImpactResults';

const getAltFieldName = (fieldName: string): string =>
  fieldName.startsWith('opportunity') && fieldName.length > 'opportunity'.length
    ? fieldName.charAt('opportunity'.length).toLowerCase() + fieldName.slice('opportunity'.length + 1)
    : 'opportunity' + fieldName.charAt(0).toUpperCase() + fieldName.slice(1);

/**
 * Resolve a fieldPath value from an evidence object, trying all key variants
 * across cxItem and opportunity.
 *
 * For any cxItem.* field we derive both forms of the field name:
 *   - prefixed: opportunityAmount / opportunityStageName
 *   - bare:     amount / stageName
 *
 * Then we try in order:
 *   1. cxItem.<original>      — direct (already done as val)
 *   2. cxItem.<altVariant>    — the other form on cxItem
 *   3. opportunity.<original> — same name on linked-opp object
 *   4. opportunity.<altVariant>
 */
export const resolveFieldWithFallback = (data: any, fieldPath: string): any => {
  const val = getNestedFieldValue(data, fieldPath);
  if ((val === null || val === undefined || val === '') && fieldPath.startsWith('cxItem.')) {
    const fieldName = fieldPath.slice(7); // e.g. 'opportunityAmount' or 'amount'

    const altFieldName = getAltFieldName(fieldName);

    for (const path of [
      'cxItem.' + altFieldName,
      'opportunity.' + fieldName,
      'opportunity.' + altFieldName,
    ]) {
      const v = getNestedFieldValue(data, path);
      if (v !== null && v !== undefined && v !== '') return v;
    }

    return null;
  }
  return val;
};

/**
 * Fallback for numeric property configs that describe their valid range only
 * as free text in the placeholder (e.g. "Enter score (1-10)") instead of
 * structured `min`/`max` fields. Used only when the config's own `min`/`max`
 * are absent — prefer fixing the config to send them explicitly.
 */
export const parseNumericBoundsFromPlaceholder = (
  placeholder: string | undefined | null
): { min?: number; max?: number } => {
  if (!placeholder) return {};
  const match = placeholder.match(/\(\s*(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)\s*\)/);
  if (!match) return {};
  const min = parseFloat(match[1]);
  const max = parseFloat(match[2]);
  if (isNaN(min) || isNaN(max)) return {};
  return { min, max };
};

export const testDisplayIfCondition = (
  resolvedValue: any,
  operator: string,
  conditionValue: any,
): boolean => {
  switch (operator) {
  case 'exists':
    return (
      resolvedValue !== null &&
      resolvedValue !== undefined &&
      resolvedValue !== '' &&
      resolvedValue !== 'N/A'
    );
  case 'equals':
    return resolvedValue === conditionValue;
  case 'notEquals':
    return resolvedValue !== conditionValue;
  default:
    return true;
  }
};

/**
 * Evaluate a displayIf condition against an evidence object.
 *
 * For `cxItem.*` fieldPaths the evaluation is also attempted against the
 * `opportunity.*` path (the joined linkedOpportunityId document). This lets
 * the Opportunity InfoTab show for Gong-call evidences whose `cxItem.itemType`
 * is 'gong_call' but whose `opportunity.itemType` is 'opportunity'.
 */
export const evaluateDisplayIf = (data: any, displayIf: any): boolean => {
  if (!displayIf) return true;

  const primaryValue = resolveFieldWithFallback(data, displayIf.fieldPath);
  if (testDisplayIfCondition(primaryValue, displayIf.operator, displayIf.value)) return true;

  if (displayIf.fieldPath.startsWith('cxItem.')) {
    const fieldName = displayIf.fieldPath.slice(7);
    const altFieldName = getAltFieldName(fieldName);
    for (const suffix of [fieldName, altFieldName]) {
      const v = getNestedFieldValue(data, 'opportunity.' + suffix);
      if (v === null || v === undefined || v === '') continue;
      if (testDisplayIfCondition(v, displayIf.operator, displayIf.value)) return true;
    }
  }

  return false;
};
